/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/43fgc52/screen/54134918
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import equals from 'ramda/src/equals';
import values from 'ramda/src/values';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Input from '@components/@core/Input';
import { RecurringType, LibraryToDoHabitDetailMenu } from '~/services/config';

class Edit extends Component {
    state = {
        id: this.props.data.id,
        title: this.props.data.name,
        description: this.props.data.data.description,
        selected: {
            author: [this.props.data.author_id],
            department: this.props.data.data.department || [],
            competency: this.props.data.data.competency || [],
            category: this.props.data.data.category || [],
            frequency: this.props.data.data.schedule_interval ? RecurringType[`${this.props.data.data.schedule_interval}`] : null,
        },
        disabled: true,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { data } = nextProps;
        const { title, description, selected } = prevState;
        const disabled =
            data.name == title &&
            data.data.description == description &&
            equals(selected.author, [data.author_id]) &&
            equals(selected.department, data.data.department || []) &&
            equals(selected.competency, data.data.competency || []) &&
            equals(selected.category, data.data.category || []) &&
            selected.frequency == RecurringType[`${data.data.schedule_interval}`];
        return { disabled };
    }

    handleSelectFrequency = (e) => {
        const schedule = e[0];
        const { selected } = this.state;
        selected.frequency = schedule;
        this.setState({ selected });
    };

    handleSelectTags = (key) => (tags) => {
        const { selected } = this.state;
        if (!equals(selected[key], tags)) {
            selected[key] = tags;
            this.setState({ selected });
        }
    };

    handleSubmit = (published) => () => {
        const { id, title, description, selected } = this.state;
        const data = {
            template: {
                id,
                card_type_id: 8,
                name: title,
                author_id: selected.author[0],
                access_type: 1,
                published,
                data: {
                    description,
                    author: selected.author,
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    type: 'recurring',
                    schedule_end: 'never',
                    schedule_increment: 1,
                    schedule_interval: selected.frequency?.name || null,
                    schedule_options: { days: [moment().format('ddd')] },
                    schedule_start: new Date().toISOString(),
                    schedule_start_timestamp: moment().unix(),
                },
            },
        };
        const payload = { type: 'habits', data };
        this.props.onUpdate(payload);
    };

    render() {
        const { title, description, selected, disabled } = this.state;
        const { authors, departments, competencies, categories, userRole, onSelect } = this.props;
        const frequencyId = selected.frequency?.id;

        return (
            <>
                <Filter back="all habits" />
                <div className="lib-todo-habit" data-cy="todoEditHabitForm">
                    <div className="habit-detail">
                        <div className="d-flex jc-between py-2">
                            <p className="dsl-b18 bold mb-0">Edit Habit</p>
                            <EditDropdown options={LibraryToDoHabitDetailMenu[userRole]} onChange={onSelect} dataCy="todoEditHabitTopThreeDot" />
                        </div>
                        <Input
                            className="input-field"
                            title="Title"
                            dataCy="title"
                            type="text"
                            direction="vertical"
                            value={title}
                            placeholder="Type here..."
                            onChange={(e) => this.setState({ title: e })}
                        />
                        <Input
                            title="Description"
                            dataCy="description"
                            className="input-field input-textarea"
                            type="text"
                            as="textarea"
                            rows="2"
                            direction="vertical"
                            value={description}
                            placeholder="Type here..."
                            onChange={(e) => this.setState({ description: e })}
                        />
                        <Dropdown
                            title="Frequency"
                            dataCy="frequency"
                            direction="vertical"
                            width="fit-content"
                            data={values(RecurringType)}
                            defaultIds={[frequencyId]}
                            getValue={(e) => e.label}
                            returnBy="data"
                            onChange={this.handleSelectFrequency}
                        />

                        <Accordion className="settings-habit mt-3" icon="fas fa-gear">
                            <Row className="mt-1">
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field author"
                                        title="Author"
                                        direction="vertical"
                                        dataCy="author"
                                        width="fit-content"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.author}
                                        data={authors}
                                        onChange={this.handleSelectTags('author')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field categories"
                                        title="Categories"
                                        direction="vertical"
                                        dataCy="categories"
                                        width="fit-content"
                                        placeholder="Select multiple"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.category}
                                        data={categories}
                                        onChange={this.handleSelectTags('category')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field competencies"
                                        title="Competencies"
                                        direction="vertical"
                                        dataCy="competencies"
                                        width="fit-content"
                                        placeholder="Select multiple"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.competency}
                                        data={competencies}
                                        onChange={this.handleSelectTags('competency')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field dropdown-field-small department"
                                        title="Departments"
                                        direction="vertical"
                                        dataCy="department"
                                        width="fit-content"
                                        placeholder="Select multiple"
                                        getValue={(e) => e.name}
                                        defaultIds={selected.department}
                                        data={departments}
                                        onChange={this.handleSelectTags('department')}
                                    />
                                </Col>
                            </Row>
                        </Accordion>
                        <div className="mt-4 d-flex jc-end">
                            <Button className="btn-save me-3" name="Draft" disabled={disabled} type="low" onClick={this.handleSubmit(0)} />
                            <Button className="btn-save" name="Publish" disabled={disabled} onClick={this.handleSubmit(1)} />
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

Edit.propTypes = {
    tags: PropTypes.array,
    onUpdate: PropTypes.func,
};

Edit.defaultProps = {
    tags: [],
    onUpdate: () => {},
};

export default Edit;
